import { useState, useEffect, useRef, type ReactNode } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Fade,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Link as RouterLink } from "react-router-dom";
import { MarketingShell } from "../components/marketing-shell";

import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";

const SYMPL_GRADIENT = "linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)";

const heroChips = [
  "Venda com menos cliques",
  "Controle total de caixa e contas",
  "Estoque sempre atualizado",
  "Equipe organizada por permissões",
  "Painéis para acompanhar resultados",
  "IA para acelerar tarefas do dia",
];

const featureSections = [
  {
    title: "Vendas Rápidas e Inteligentes",
    description: "Atenda seus clientes sem lentidão. O fluxo foi pensado para quem está no ritmo do balcão.",
    icon: StorefrontRoundedIcon,
    items: [
      {
        title: "Tela de venda com copiloto",
        description: "Adicione itens, visualize sugestões laterais e finalize em poucos cliques.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.37.39-52dbb28b-d043-4b6d-93df-611219aa5940.png"
      },
      {
        title: "Sugestões por texto",
        description: "Digite o que o cliente pediu e o sistema sugere o produto exato do seu estoque.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.40.34-73b4688f-b5bc-43fe-b1e2-ac97214d6a3e.png"
      },
      {
        title: "Vendas em aberto",
        description: "Deixe a venda em espera e atenda o próximo cliente sem perder as informações.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.41.33-4288ff3f-a1fb-425d-a4ac-fbb2ef56034a.png"
      }
    ]
  },
  {
    title: "Clientes e Controle de Fiado",
    description: "Saiba exatamente quem deve, quanto deve e o histórico de cada cliente.",
    icon: PeopleRoundedIcon,
    items: [
      {
        title: "Visão geral da carteira",
        description: "Identifique rapidamente clientes com saldo devedor e o total a receber.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.02.20-dce666af-4cf0-4bd3-97bc-b9ca09b1745c.png"
      },
      {
        title: "Detalhe e histórico",
        description: "Abra a ficha do cliente para ver todas as compras, pagamentos parciais e o histórico do fiado.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.02.42-cbfb1614-e81f-43c3-989a-abf5acb04b12.png"
      }
    ]
  },
  {
    title: "Gestão de Estoque Simplificada",
    description: "Não perca vendas por falta de produto. O Symplou te avisa quando o estoque está crítico.",
    icon: InventoryRoundedIcon,
    items: [
      {
        title: "Painel de alertas",
        description: "Veja itens zerados, acabando ou com estoque saudável em uma única tela.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.03.50-d87f78ed-c80e-4d9a-9a86-fdcf6f377351.png"
      }
    ]
  },
  {
    title: "Financeiro (Contas e Caixa)",
    description: "Controle as contas a pagar, recebimentos e o fechamento do caixa sem planilhas confusas.",
    icon: AccountBalanceWalletRoundedIcon,
    items: [
      {
        title: "Controle de caixa diário",
        description: "Abra o caixa, registre sangrias, suprimentos e veja o saldo em tempo real.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.46.59-590e72d4-142e-4b26-abe8-89f51cecc256.png"
      },
      {
        title: "Contas a pagar",
        description: "Acompanhe os vencimentos de boletos e faturas para não perder prazos e evitar juros.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.46.00-56c68fc6-fde6-419e-b683-ae8148b9de12.png"
      },
      {
        title: "Nova conta a pagar",
        description: "Cadastre novas despesas e fornecedores de forma ágil, organizando o fluxo de caixa.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.04.59-127513ef-dbe6-45ef-8608-0409d16aa038.png"
      },
      {
        title: "Relatórios de fechamento",
        description: "Audite sessões anteriores e confira os fechamentos de turnos dos seus operadores.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.04.45-ee50e517-9129-4b06-932c-d3bf14e2a1ef.png"
      }
    ]
  },
  {
    title: "Comissões e Equipe",
    description: "Cada pessoa vê só o que precisa, e as comissões são calculadas automaticamente sem esforço.",
    icon: GroupsRoundedIcon,
    items: [
      {
        title: "Painel de comissões",
        description: "Visualize o total vendido e o valor a pagar para cada colaborador no mês.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.45.24-af27f7b0-bf33-4742-8ed6-3bfea5add618.png"
      },
      {
        title: "Gestão de equipe",
        description: "Liste todos os usuários, visualize cargos, e gerencie acessos em poucos cliques.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.44.16-a68cc2f3-e00c-470c-b71c-759db70d8a9e.png"
      },
      {
        title: "Controle de permissões",
        description: "Restrinja o acesso e garanta que dados financeiros não sejam vistos por quem não deve.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.44.29-a12d29d2-11f2-442a-90c5-630ec93ab2b2.png"
      }
    ]
  },
  {
    title: "Automação com IA",
    description: "A Inteligência Artificial do Symplou poupa horas do seu dia automatizando tarefas repetitivas.",
    icon: AutoAwesomeRoundedIcon,
    items: [
      {
        title: "Cadastro em massa",
        description: "Edite vários produtos numa interface de planilha extremamente rápida.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.01.58-7b714761-1f38-484a-857b-3299a47de31f.png"
      },
      {
        title: "Otimização de fotos",
        description: "A IA melhora a qualidade da imagem, remove o fundo e cria um padrão de loja virtual.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.57.02-8a29b37e-aa75-4b76-ab5a-0cdb69596c5b.png"
      }
    ]
  },
  {
    title: "Visão Estratégica (Dashboard)",
    description: "Acompanhe de perto as estatísticas vitais e as configurações que regem o seu negócio.",
    icon: DashboardRoundedIcon,
    items: [
      {
        title: "Estatísticas de Vendas",
        description: "Dashboard dinâmico que mostra suas receitas, ticket médio e gráficos de desempenho.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.03.27-188b54fe-9f3c-4e15-9754-a1b2223c5639.png"
      },
      {
        title: "Configurações Globais",
        description: "Ajuste configurações da loja, tributação, métodos de pagamento e categorias.",
        imageSrc: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.04.13-99003897-8961-4d00-84a1-256fdc34892f.png"
      }
    ]
  }
];

const useInView = (threshold = 0.12) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold, rootMargin: "0px 0px -60px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const RevealOnScroll = ({ children, delay = 0 }: { children: ReactNode; delay?: number }) => {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "none" : "translateY(44px)",
        transition: `opacity 0.8s cubic-bezier(0.4,0,0.2,1) ${delay}ms, transform 0.8s cubic-bezier(0.4,0,0.2,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const FeatureInteractiveSection = ({ section, reversed }: { section: typeof featureSections[0], reversed: boolean }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <Box sx={{ mb: { xs: 8, md: 16 } }}>
      <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center" direction={reversed ? 'row-reverse' : 'row'}>
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  background: "linear-gradient(135deg, rgba(20,184,166,0.2) 0%, rgba(13,148,136,0.1) 100%)",
                  border: "1px solid rgba(45,212,191,0.2)",
                  color: "#2dd4bf",
                  display: 'flex'
                }}
              >
                <section.icon fontSize="large" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 900, color: "#f8fafc", lineHeight: 1.2, fontSize: { xs: "1.3rem", md: "2.125rem" } }}>
                {section.title}
              </Typography>
            </Stack>

            <Typography sx={{ color: "rgba(226,232,240,0.8)", fontSize: { xs: "1rem", sm: "1.15rem" }, lineHeight: 1.6 }}>
              {section.description}
            </Typography>

            <Stack spacing={2} sx={{ mt: 3 }}>
              {section.items.map((item, index) => {
                const isActive = activeIndex === index;
                return (
                  <Box
                    key={item.title}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => setActiveIndex(index)}
                    sx={{
                      p: 2.5,
                      borderRadius: 4,
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      border: "1px solid",
                      borderColor: isActive ? "rgba(45,212,191,0.5)" : "rgba(255,255,255,0.03)",
                      bgcolor: isActive ? "rgba(15,23,42,0.8)" : "transparent",
                      boxShadow: isActive ? "0 12px 32px rgba(0,0,0,0.3)" : "none",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "4px",
                        background: isActive ? SYMPL_GRADIENT : "transparent",
                        transition: "all 0.3s ease",
                      },
                      "&:hover": {
                        bgcolor: isActive ? "rgba(15,23,42,0.8)" : "rgba(30,41,59,0.5)",
                        borderColor: isActive ? "rgba(45,212,191,0.5)" : "rgba(255,255,255,0.1)",
                      }
                    }}
                  >
                    <Typography sx={{ color: isActive ? "#2dd4bf" : "#f8fafc", fontWeight: 800, mb: 0.8, fontSize: "1.1rem" }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: isActive ? "rgba(226,232,240,0.9)" : "rgba(148,163,184,0.8)", lineHeight: 1.5 }}>
                      {item.description}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} md={7}>
          <Box
            sx={{
              position: "relative",
              borderRadius: 5,
              overflow: "hidden",
              border: "1px solid rgba(20,184,166,0.25)",
              boxShadow: "0 32px 80px rgba(0,0,0,0.5), 0 0 40px rgba(13,148,136,0.1)",
              aspectRatio: "16 / 10",
              bgcolor: "#0f172a",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.05)",
                pointerEvents: "none",
                borderRadius: 5,
              }
            }}
          >
            {section.items.map((item, index) => (
              <Fade in={activeIndex === index} timeout={600} key={item.imageSrc}>
                <Box
                  component="img"
                  src={item.imageSrc}
                  alt={item.title}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: activeIndex === index ? "block" : "none",
                    transition: "transform 0.6s ease-out",
                    transform: activeIndex === index ? "scale(1)" : "scale(1.05)",
                  }}
                />
              </Fade>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const HERO_SLIDES = [
  { src: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.37.39-52dbb28b-d043-4b6d-93df-611219aa5940.png", label: "Vendas com copiloto" },
  { src: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.03.27-188b54fe-9f3c-4e15-9754-a1b2223c5639.png", label: "Estatísticas de vendas" },
  { src: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.02.20-dce666af-4cf0-4bd3-97bc-b9ca09b1745c.png", label: "Clientes e fiado" },
  { src: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.46.59-590e72d4-142e-4b26-abe8-89f51cecc256.png", label: "Controle de caixa" },
  { src: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.46.00-56c68fc6-fde6-419e-b683-ae8148b9de12.png", label: "Contas a pagar" },
  { src: "/marketing/Captura_de_Tela_2026-04-30_a_s_18.03.50-d87f78ed-c80e-4d9a-9a86-fdcf6f377351.png", label: "Gestão de estoque" },
  { src: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.45.24-af27f7b0-bf33-4742-8ed6-3bfea5add618.png", label: "Comissões" },
  { src: "/marketing/Captura_de_Tela_2026-04-30_a_s_17.44.16-a68cc2f3-e00c-470c-b71c-759db70d8a9e.png", label: "Gestão de equipe" },
];

export const MarketingHomePage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const isPaused = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isPaused.current) {
        setActiveSlide(i => (i + 1) % HERO_SLIDES.length);
      }
    }, 3800);
    return () => clearInterval(timer);
  }, []);

  return (
    <MarketingShell fullBleed>

      {/* ─── HERO FULLSCREEN SPLIT ─── */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-10%",
            right: "-5%",
            width: "65%",
            height: "130%",
            background: "radial-gradient(ellipse at 80% 40%, rgba(13,148,136,0.18) 0%, rgba(20,184,166,0.05) 50%, transparent 70%)",
            zIndex: 0,
            pointerEvents: "none",
          },
        }}
      >
        <Grid
          container
          alignItems="center"
          sx={{ position: "relative", zIndex: 1, width: "100%", px: { xs: 3, md: 6, lg: 10 } }}
        >
          {/* ── Left: text ── */}
          <Grid item xs={12} md={7}>
            <Box sx={{ py: { xs: 5, md: 0 }, pr: { md: 6 } }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  background: "rgba(13,148,136,0.14)",
                  border: "1px solid rgba(20,184,166,0.28)",
                  borderRadius: 8,
                  px: 2.5,
                  py: 0.7,
                  mb: 4,
                }}
              >
                <Typography
                  sx={{
                    color: "#2dd4bf",
                    fontWeight: 800,
                    fontSize: "0.8rem",
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                  }}
                >
                  Sistema de Gestão Completo
                </Typography>
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.07,
                  letterSpacing: "-0.035em",
                  fontSize: { xs: "2rem", sm: "3.4rem", md: "4rem", lg: "4.8rem" },
                  color: "#f8fafc",
                  mb: 3,
                }}
              >
                O Symplou
                <br />
                trabalha{" "}
                <Box
                  component="span"
                  sx={{
                    background: SYMPL_GRADIENT,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  por você
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: "rgba(226,232,240,0.7)",
                  fontWeight: 400,
                  lineHeight: 1.7,
                  mb: 5,
                  fontSize: { xs: "1.05rem", md: "1.15rem" },
                  maxWidth: 520,
                }}
              >
                Vendas rápidas, estoque sempre atualizado e financeiro transparente.
                Controle total do seu negócio com IA para acelerar sua rotina.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 5.5 }}>
                <Button
                  component={RouterLink}
                  to="/planos"
                  size="large"
                  variant="contained"
                  sx={{
                    background: SYMPL_GRADIENT,
                    fontWeight: 800,
                    px: 5,
                    py: 1.7,
                    fontSize: "1.05rem",
                    borderRadius: 3,
                    boxShadow: "0 8px 32px rgba(13,148,136,0.45)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      background: "linear-gradient(135deg, #0f766e 0%, #14b8a6 50%, #0d9488 100%)",
                      boxShadow: "0 14px 44px rgba(13,148,136,0.65)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Começar agora
                </Button>
                <Button
                  component="a"
                  href="#recursos"
                  size="large"
                  variant="outlined"
                  sx={{
                    color: "#f8fafc",
                    borderColor: "rgba(20,184,166,0.4)",
                    fontWeight: 700,
                    px: 5,
                    py: 1.7,
                    fontSize: "1.05rem",
                    borderRadius: 3,
                    transition: "transform 0.2s ease",
                    "&:hover": {
                      background: "rgba(13,148,136,0.1)",
                      borderColor: "#14b8a6",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Ver recursos
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {heroChips.map((item, i) => (
                  <Chip
                    key={item}
                    icon={
                      <CheckCircleRoundedIcon
                        sx={{ color: "#2dd4bf !important", fontSize: "1rem" }}
                      />
                    }
                    label={item}
                    sx={{
                      display: { xs: i >= 3 ? "none" : "flex", sm: "flex" },
                      bgcolor: "rgba(15,23,42,0.55)",
                      color: "rgba(226,232,240,0.82)",
                      border: "1px solid rgba(13,148,136,0.22)",
                      fontWeight: 600,
                      fontSize: "0.84rem",
                      height: 32,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* ── Right: 3D slider ── */}
          <Grid item xs={12} md={5} sx={{ display: { xs: "none", md: "block" } }}>
            <Box
              sx={{ position: "relative", width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start" }}
              onMouseEnter={() => { isPaused.current = true; }}
              onMouseLeave={() => { isPaused.current = false; }}
            >
              {/* 3D image slider */}
              <Box
                sx={{
                  position: "relative",
                  width: "140%",
                  maxWidth: 840,
                  aspectRatio: "16 / 10",
                  borderRadius: 4,
                  overflow: "hidden",
                  border: "1px solid rgba(45,212,191,0.2)",
                  boxShadow: "-12px 32px 80px rgba(0,0,0,0.6), 0 0 40px rgba(13,148,136,0.08)",
                  bgcolor: "#0f172a",
                  transform: "perspective(1200px) rotateY(-15deg) rotateX(4deg)",
                  transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "perspective(1200px) rotateY(-5deg) rotateX(1deg)",
                  },
                }}
              >
                {HERO_SLIDES.map((slide, i) => (
                  <Fade in={activeSlide === i} timeout={{ enter: 900, exit: 500 }} key={slide.src}>
                    <Box
                      component="img"
                      src={slide.src}
                      alt={slide.label}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "top left",
                      }}
                    />
                  </Fade>
                ))}
              </Box>

              {/* Dots + label */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2.5, pl: 0.5 }}>
                {HERO_SLIDES.map((slide, i) => (
                  <Box
                    key={i}
                    onClick={() => setActiveSlide(i)}
                    title={slide.label}
                    sx={{
                      width: i === activeSlide ? 22 : 7,
                      height: 7,
                      borderRadius: 4,
                      bgcolor: i === activeSlide ? "#2dd4bf" : "rgba(148,163,184,0.25)",
                      cursor: "pointer",
                      transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
                      flexShrink: 0,
                      "&:hover": {
                        bgcolor: i === activeSlide ? "#2dd4bf" : "rgba(148,163,184,0.5)",
                      },
                    }}
                  />
                ))}
                <Typography
                  sx={{
                    color: "rgba(148,163,184,0.55)",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    ml: 1,
                    letterSpacing: "0.02em",
                    transition: "opacity 0.3s",
                  }}
                >
                  {HERO_SLIDES[activeSlide].label}
                </Typography>
              </Stack>

              {/* Progress bar */}
              <Box
                sx={{
                  width: "140%",
                  maxWidth: 840,
                  height: "2px",
                  bgcolor: "rgba(148,163,184,0.08)",
                  borderRadius: 1,
                  mt: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  key={activeSlide}
                  sx={{
                    height: "100%",
                    bgcolor: "rgba(45,212,191,0.45)",
                    borderRadius: 1,
                    "@keyframes heroProgress": {
                      from: { width: "0%" },
                      to: { width: "100%" },
                    },
                    animation: "heroProgress 3.8s linear forwards",
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* ── Scroll indicator ── */}
        <Box
          sx={{
            position: "absolute",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              color: "rgba(148,163,184,0.4)",
              display: "flex",
              alignItems: "center",
              "@keyframes scrollBounce": {
                "0%, 100%": { transform: "translateY(0)", opacity: 0.4 },
                "50%": { transform: "translateY(9px)", opacity: 0.85 },
              },
              animation: "scrollBounce 2.4s ease-in-out infinite",
            }}
          >
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: "2.2rem" }} />
          </Box>
        </Box>
      </Box>

      {/* ─── FEATURES ─── */}
      <Box id="recursos" sx={{ scrollMarginTop: "80px" }}>
        <Container
          maxWidth="xl"
          disableGutters
          sx={{ px: { xs: 2, md: 5 }, pt: { xs: 5, md: 12 }, pb: { xs: 8, md: 12 } }}
        >
          <RevealOnScroll>
            <Stack spacing={2} sx={{ mb: { xs: 5, md: 10 }, textAlign: "center", alignItems: "center" }}>
              <Box
                sx={{
                  background: "rgba(13,148,136,0.15)",
                  borderRadius: 4,
                  px: 2,
                  py: 0.5,
                  border: "1px solid rgba(20,184,166,0.3)",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: "#2dd4bf", fontWeight: 800, letterSpacing: "0.15em" }}
                >
                  Conheça o Symplou
                </Typography>
              </Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  color: "#f8fafc",
                  maxWidth: 900,
                  lineHeight: 1.2,
                  fontSize: { xs: "2rem", md: "3rem" },
                }}
              >
                Como cada área do sistema trabalha a favor do seu negócio
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(226,232,240,0.7)",
                  maxWidth: 760,
                  fontSize: "1.2rem",
                  mt: 2,
                }}
              >
                A proposta do Symplou é simples: conectar tudo. Do balcão de vendas até o
                controle de contas, você tem a visão completa e interativa que precisa.
              </Typography>
            </Stack>
          </RevealOnScroll>

          <Box>
            {featureSections.map((section, index) => (
              <RevealOnScroll key={section.title}>
                <FeatureInteractiveSection
                  section={section}
                  reversed={index % 2 !== 0}
                />
              </RevealOnScroll>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ─── CTA ─── */}
      <Container
        maxWidth="xl"
        disableGutters
        sx={{ px: { xs: 2, md: 5 }, pb: { xs: 10, md: 16 } }}
      >
        <RevealOnScroll>
          <Box
            sx={{
              p: { xs: 4, md: 8 },
              borderRadius: 5,
              position: "relative",
              overflow: "hidden",
              background: "#0f172a",
              border: "1px solid rgba(20,184,166,0.4)",
              boxShadow: "0 32px 80px rgba(13,148,136,0.25)",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: SYMPL_GRADIENT,
                opacity: 0.15,
                zIndex: 0,
              }}
            />
            <Box sx={{ position: "relative", zIndex: 1, maxWidth: 800, mx: "auto" }}>
              <Typography variant="h3" sx={{ fontWeight: 900, color: "#f8fafc", mb: 3 }}>
                Pronto para ter mais controle e mais resultado no dia a dia?
              </Typography>
              <Typography
                sx={{
                  color: "rgba(226,232,240,0.85)",
                  fontSize: "1.2rem",
                  mb: 5,
                  lineHeight: 1.6,
                }}
              >
                Escolha os módulos que fazem sentido para o seu momento e comece com uma
                rotina mais organizada, eficiente e livre de surpresas desagradáveis desde
                a primeira semana.
              </Typography>
              <Divider sx={{ borderColor: "rgba(148,163,184,0.25)", mb: 4 }} />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2.5}
                justifyContent="center"
              >
                <Button
                  component={RouterLink}
                  to="/planos"
                  variant="contained"
                  size="large"
                  sx={{
                    background: SYMPL_GRADIENT,
                    fontWeight: 800,
                    px: 6,
                    py: 2,
                    fontSize: "1.1rem",
                    boxShadow: "0 8px 24px rgba(13,148,136,0.4)",
                    "&:hover": { boxShadow: "0 12px 32px rgba(13,148,136,0.6)" },
                  }}
                >
                  Ver planos e módulos
                </Button>
                <Button
                  component={RouterLink}
                  to="/entrar"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "rgba(20,184,166,0.5)",
                    color: "#f8fafc",
                    fontWeight: 800,
                    px: 6,
                    py: 2,
                    fontSize: "1.1rem",
                    "&:hover": {
                      borderColor: "#14b8a6",
                      background: "rgba(13,148,136,0.15)",
                    },
                  }}
                >
                  Entrar no sistema
                </Button>
              </Stack>
            </Box>
          </Box>
        </RevealOnScroll>
      </Container>
    </MarketingShell>
  );
};
